import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  fallback: ReactNode | ((error: Error) => ReactNode);
  children?: ReactNode;
  track: (message: string) => void;
}

interface State {
  error?: Error;
}

/**
 * Implements a basic React Error Boundary
 * https://reactjs.org/docs/error-boundaries.html
 * 
 * Accepts either a function to render an `Error` as a `ReactNode`,
 * or a plain `ReactNode`
 * 
 * Errors are also logged to console.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    error: undefined
  };

  public static getDerivedStateFromError = (error: Error): State => {
    // Update state so the next render will show the fallback UI.
    return { error: error };
  };

  public componentDidCatch = (error: Error, errorInfo: ErrorInfo) => {
    const message = `GraphQL Error: ${error.name}, ${error.message}, ${error.stack}, ${errorInfo.componentStack}`;
    this.props.track(message);
  };

  public render = () => {
    if (this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error);
      } else {
        return this.props.fallback;
      }
    }

    return this.props.children;
  };
}

export default ErrorBoundary;